import { supabase } from 'common/src/supabase/index';

export const getSettlementHistory = async (settlementId: number) => {
  try {
    const { data, error } = await supabase
      .from('history_settlements')
      .select(
        `
        *,
        created_by:users!history_settlements_created_by_fkey(
          id,
          first_name,
          last_name
        )
      `
      )
      .eq('settlement_id', settlementId)
      .is('deleted_at', null)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error fetching settlement history:', error);
    throw error;
  }
};

export const getSettlementHistoryCounts = async (settlementIds: number[]) => {
  if (settlementIds.length === 0) return new Map<number, number>();

  try {
    const { data, error } = await supabase
      .from('history_settlements')
      .select('settlement_id')
      .in('settlement_id', settlementIds)
      .is('deleted_at', null);

    if (error) throw error;

    const counts = new Map<number, number>();
    for (const row of data || []) {
      const settlementId = Number(row.settlement_id);
      counts.set(settlementId, (counts.get(settlementId) || 0) + 1);
    }
    return counts;
  } catch (error) {
    console.error('Error fetching settlement history counts:', error);
    throw error;
  }
};

export const addHistoryComment = async (
  settlementId: number,
  content: string,
  userId: number,
  type: 'comment' | 'document' | 'action' = 'comment'
) => {
  const { data, error } = await supabase
    .from('history_settlements')
    .insert({
      settlement_id: settlementId,
      content,
      created_by: userId,
      type,
    })
    .select(
      `
      id,
      content,
      type,
      created_at,
      is_edited,
      created_by:users!history_settlements_created_by_fkey(
        id,
        first_name,
        last_name
      )
    `
    )
    .single();

  if (error) throw error;
  return data;
};

export const updateHistoryComment = async (
  commentId: number,
  content: string
) => {
  try {
    const { data, error } = await supabase
      .from('history_settlements')
      .update({ content })
      .eq('id', commentId)
      .select(
        `
        *,
        created_by:users!history_settlements_created_by_fkey(
          id,
          first_name,
          last_name
        )
      `
      )
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error updating comment:', error);
    throw error;
  }
};

export const deleteHistoryItem = async (itemId: number) => {
  try {
    const { error } = await supabase
      .from('history_settlements')
      .update({ deleted_at: new Date().toISOString() })
      .eq('id', itemId);

    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Error deleting history item:', error);
    throw error;
  }
};

export const addHistoryDocument = async (
  settlementId: number,
  title: string,
  fileName: string,
  filePath: string,
  userId: number
) => {
  try {
    const { data, error } = await supabase
      .from('history_settlements')
      .insert({
        settlement_id: settlementId,
        type: 'document',
        title,
        file_name: fileName,
        file_path: filePath,
        created_by: userId,
      })
      .select(
        `
        *,
        created_by:users!history_settlements_created_by_fkey(
          id,
          first_name,
          last_name
        )
      `
      )
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error adding document:', error);
    throw error;
  }
};

export const uploadDocumentFile = async (file: File, path: string) => {
  try {
    const { data, error } = await supabase.storage
      .from('settlements_documents')
      .upload(path, file);

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error uploading file:', error);
    throw error;
  }
};

export const getDocumentUrl = async (
  path: string,
  bucket = 'settlements_documents'
) => {
  try {
    const { data } = await supabase.storage
      .from(bucket)
      .createSignedUrl(path, 3600); // URL válida por 1 hora

    return data?.signedUrl;
  } catch (error) {
    console.error('Error getting document URL:', error);
    throw error;
  }
};

export interface SettlementOriginalDocument {
  historyItemId: number;
  sourceDocumentId: string;
  fileName: string;
  filePath: string;
  bucket: string;
  url: string;
}

export const getSettlementOriginalDocument = async (
  settlementId: number
): Promise<SettlementOriginalDocument | null> => {
  const history = await getSettlementHistory(settlementId);
  const originalDocument = history.find(
    (item) =>
      item.type === 'document' && item.source_document_id && item.file_path
  );

  if (!originalDocument?.source_document_id || !originalDocument.file_path) {
    return null;
  }

  const bucket = originalDocument.storage_bucket || 'settlements_documents';
  const url = await getDocumentUrl(originalDocument.file_path, bucket);
  if (!url) return null;

  return {
    historyItemId: originalDocument.id,
    sourceDocumentId: originalDocument.source_document_id,
    fileName:
      originalDocument.file_name ||
      originalDocument.title ||
      'Documento original',
    filePath: originalDocument.file_path,
    bucket,
    url,
  };
};
